import { useEffect, useMemo, useRef } from "react"
import QRCodeStyling from "qr-code-styling"

const PRESETS = {
  clinical: {
    dots:   { type: "square",  color: "#111827" },
    eyeSq:  { type: "square",  color: "#111827" },
    eyeDot: { type: "square",  color: "#111827" },
    bg: "#FFFFFF",
    margin: 12,
    wrapper: "bg-white rounded-2xl p-4 ring-1 ring-slate-200 shadow-[0_0_12px_rgba(2,6,23,0.06)]",
    logoMargin: 10,
    hideBgDots: true,
  },
  brand: {
    dots: {
      type: "rounded",
      color: "#0F172A",
      gradient: {
        type: "linear",
        rotation: 0,
        colorStops: [
          { offset: 0, color: "#6366F1" },
          { offset: 1, color: "#22D3EE" },
        ],
      },
    },
    eyeSq:  { type: "extra-rounded", color: "#0F172A" },
    eyeDot: { type: "dot",           color: "#0F172A" },
    bg: "#FFFFFF",
    margin: 10,
    wrapper: "bg-white rounded-2xl p-4 ring-1 ring-slate-200 shadow-[0_0_12px_rgba(2,6,23,0.06)]",
    logoMargin: 8,
    hideBgDots: true,
  },
  duotone: {
    dots:   { type: "square", color: "#0B1220" },
    eyeSq:  { type: "square", color: "#1E293B" },
    eyeDot: { type: "dot",    color: "#6366F1" },
    bg: "#FFFFFF",
    margin: 12,
    wrapper: "bg-white rounded-2xl p-4 ring-1 ring-slate-200 shadow-[0_0_12px_rgba(2,6,23,0.06)]",
    logoMargin: 8,
    hideBgDots: true,
  },
  dots: {
    dots:   { type: "dots",   color: "#0F172A" },
    eyeSq:  { type: "rounded", color: "#0F172A" },
    eyeDot: { type: "dot",     color: "#0F172A" },
    bg: "#FFFFFF",
    margin: 12,
    wrapper: "bg-white rounded-2xl p-4 ring-1 ring-slate-200 shadow-[0_0_12px_rgba(2,6,23,0.06)]",
    logoMargin: 8,
    hideBgDots: true,
  },
  pill: {
    dots:   { type: "rounded", color: "#0F172A" },
    eyeSq:  { type: "extra-rounded", color: "#0F172A" },
    eyeDot: { type: "dot", color: "#0F172A" },
    bg: "#FFFFFF",
    margin: 14,
    wrapper: "bg-white rounded-[28px] p-6 ring-1 ring-slate-200 shadow-[0_0_12px_rgba(2,6,23,0.06)]",
    logoMargin: 10,
    hideBgDots: true,
  },
  glass: {
    dots:   { type: "square", color: "#0F172A" },
    eyeSq:  { type: "square", color: "#0F172A" },
    eyeDot: { type: "dot",    color: "#0F172A" },
    bg: "transparent",
    margin: 12,
    wrapper:
      "backdrop-blur-md bg-white/50 rounded-2xl p-5 ring-1 ring-white/60 shadow-[0_0_12px_rgba(2,6,23,0.06)] " +
      "bg-[radial-gradient(120%_120%_at_0%_0%,#C7D2FE_0%,transparent_40%)," +
      "radial-gradient(120%_120%_at_100%_100%,#99F6E4_0%,transparent_40%)]",
    logoMargin: 8,
    hideBgDots: true,
  },
}

const DEFAULT_LOGO_SCALE = { L: 0.20, M: 0.24, Q: 0.28, H: 0.32 }

/**
 * Fancy QR with styling presets and controllable ECC + logo size.
 * - ecl: "L" | "M" | "Q" | "H"
 * - logoScale: overrides default (0..1, portion of QR width)
 */
export default function FancyQr({
  data,
  size = 320,
  className = "",
  logo = "/icons/oshimed-mark.png",
  preset = "clinical",
  noLogo = false,
  ecl = "L",
  logoScale,         
}) {
  const containerRef = useRef(null)
  const qrRef = useRef(null)          
  const p = PRESETS[preset] || PRESETS.clinical


  const snappedSize = useMemo(() => Math.floor(size / 2) * 2, [size])
  const effectiveLogoScale = logoScale ?? (DEFAULT_LOGO_SCALE[ecl] || DEFAULT_LOGO_SCALE.M)


  useEffect(() => {
    if (!containerRef.current || qrRef.current) return

    qrRef.current = new QRCodeStyling({
      width: snappedSize,
      height: snappedSize,
      type: "svg",
      data: data || "",
      qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: ecl, margin: p.margin },
      backgroundOptions: { color: p.bg },
      image: noLogo ? undefined : logo,
      imageOptions: {
        crossOrigin: "anonymous",
        margin: p.logoMargin,
        hideBackgroundDots: p.hideBgDots,
        imageSize: effectiveLogoScale,   
      },
      dotsOptions: p.dots,
      cornersSquareOptions: p.eyeSq,
      cornersDotOptions: p.eyeDot,
    })

  
    containerRef.current.innerHTML = ""
    qrRef.current.append(containerRef.current)

    const el = containerRef.current.querySelector("svg,canvas")
    if (el) {
      el.style.display = "block"
      el.style.margin = "0 auto"
      if (el.tagName.toLowerCase() === "svg") el.setAttribute("shape-rendering", "crispEdges")
    }
  }, [])

  // Update on any prop change
  useEffect(() => {
    if (!qrRef.current) return
    qrRef.current.update({
      width: snappedSize,
      height: snappedSize,
      data: data || "",
      qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: ecl, margin: p.margin },
      backgroundOptions: { color: p.bg },
      image: noLogo ? undefined : logo,
      imageOptions: {
        crossOrigin: "anonymous",
        margin: p.logoMargin,
        hideBackgroundDots: p.hideBgDots,
        imageSize: effectiveLogoScale,
      },
      dotsOptions: p.dots,
      cornersSquareOptions: p.eyeSq,
      cornersDotOptions: p.eyeDot,
    })
  }, [data, snappedSize, ecl, p, logo, noLogo, effectiveLogoScale])

  return (
    <div
      ref={containerRef}
      className={`mx-auto inline-grid place-items-center leading-none
        ${p.wrapper} print:shadow-none print:ring-0
        [&>svg]:block [&>canvas]:block ${className}`}
      style={{ lineHeight: 0 }}
    />
  )
}
